import React, { useState, useEffect } from 'react';
import { Star, Heart, Briefcase, Activity, ArrowRight, Sparkles, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLang } from '../context/LanguageContext';
import VaidikUpayCard from './VaidikUpayCard';

const API = process.env.REACT_APP_BACKEND_URL;

const ELEMENT_FROM_NAME = {
  Aries: 'Fire', Leo: 'Fire', Sagittarius: 'Fire',
  Taurus: 'Earth', Virgo: 'Earth', Capricorn: 'Earth',
  Gemini: 'Air', Libra: 'Air', Aquarius: 'Air',
  Cancer: 'Water', Scorpio: 'Water', Pisces: 'Water',
};

const elementBg = {
  Fire: 'from-red-500/15 to-amber-500/10 border-red-500/20',
  Earth: 'from-emerald-500/15 to-green-600/10 border-green-500/20',
  Air: 'from-blue-400/15 to-cyan-400/10 border-blue-400/20',
  Water: 'from-indigo-500/15 to-purple-light/10 border-indigo-500/20',
};

const ratingStars = (r) => {
  const stars = [];
  for (let i = 0; i < 10; i++) {
    stars.push(<Star key={i} className={`w-3.5 h-3.5 ${i < (r || 0) ? 'text-gold fill-gold' : 'text-white/15'}`} />);
  }
  return stars;
};

export default function DailyHoroscope() {
  const [sel, setSel] = useState(null);
  const [rashifal, setRashifal] = useState([]);
  const [dateHi, setDateHi] = useState('');
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLang();

  useEffect(() => {
    axios.get(`${API}/api/daily-rashifal`)
      .then(res => {
        setRashifal(res.data.rashifal || []);
        setDateHi(res.data.date_hi || '');
      })
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, []);

  const todayStr = dateHi || new Date().toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const h = sel !== null ? rashifal[sel] : null;
  const element = h ? ELEMENT_FROM_NAME[h.name_en] || 'Water' : 'Water';

  return (
    <section id="horoscope" className="section-padding relative overflow-hidden" data-testid="horoscope-section">
      <div className="absolute inset-0 cosmic-bg" />
      <div className="absolute top-1/3 left-0 w-80 h-80 bg-gold/5 rounded-full blur-[120px]" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-4">{t('horoscope.tag')}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mb-4 tracking-tight">
            {t('horoscope.title1')} <span className="text-gold-gradient">{t('horoscope.title2')}</span>
          </h2>
          <p className="text-text-muted text-sm">{todayStr}</p>
          <p className="text-text-muted/50 text-xs mt-1 font-heading italic">{t('horoscope.selectSign')}</p>
          <div className="divine-divider mt-6 max-w-xs mx-auto" />
        </div>

        {/* Zodiac Grid */}
        {loading ? (
          <div className="text-center text-text-muted py-8" data-testid="horoscope-loading">
            {lang === 'hi' ? 'राशिफल तैयार हो रहा है... आचार्य गणनाएं कर रहे हैं' : 'Loading horoscope...'}
          </div>
        ) : rashifal.length === 0 ? (
          <div className="text-center text-text-muted py-8">
            <Link to="/daily-rashifal" className="text-gold underline">
              {lang === 'hi' ? 'पूरा राशिफल देखें' : 'See full horoscope'} →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3 mb-10">
            {rashifal.map((z, i) => (
              <motion.button key={z.name_en} onClick={() => setSel(i)} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
                className={`relative flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-all duration-300 cursor-pointer
                  ${sel === i ? 'bg-gradient-to-br from-gold/15 to-purple/10 border-gold/40 shadow-lg shadow-gold/10 scale-105' : 'bg-white/3 border-white/8 hover:border-gold/20'}
                  ${z.is_critical ? 'border-red-500/40' : ''}`}
                data-testid={`zodiac-${(z.name_en || '').toLowerCase()}`}>
                {z.is_critical && (
                  <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500/90 text-white text-[10px] font-bold animate-pulse">!</span>
                )}
                <span className="text-3xl">{z.symbol}</span>
                <span className={`text-xs font-medium ${sel === i ? 'text-gold' : 'text-text-muted'}`}>{lang === 'hi' ? z.name : z.name_en}</span>
              </motion.button>
            ))}
          </div>
        )}

        {h ? (
          <motion.div className="max-w-4xl mx-auto" data-testid="horoscope-result"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} key={sel}>
            <div className="glass-card p-8">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${elementBg[element]} border flex items-center justify-center`}>
                  <span className="text-4xl">{h.symbol}</span>
                </div>
                <div className="text-center md:text-left flex-1">
                  <h3 className="font-heading text-3xl text-text-main">
                    {lang === 'hi' ? h.name : h.name_en} <span className="text-gold">({lang === 'hi' ? h.name_en : h.name})</span>
                  </h3>
                  <div className="flex items-center justify-center md:justify-start gap-3 mt-1.5 flex-wrap">
                    <div className="flex gap-0.5">{ratingStars(h.rating)}</div>
                    {h.is_critical && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-semibold">
                        <AlertTriangle className="w-3 h-3" /> {lang === 'hi' ? 'विशेष ग्रह योग' : 'Critical Transit'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-text-main text-base sm:text-lg leading-[1.85] mb-6 whitespace-pre-line">{h.prediction}</p>

              {h.shloka && (
                <div className="mb-6 bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-l-4 border-gold rounded-r-xl p-4">
                  <div className="text-xs uppercase tracking-widest text-gold mb-1 font-semibold flex items-center gap-2">
                    🕉️ {lang === 'hi' ? 'पराशरीय श्लोक' : 'Parashari Shloka'}
                  </div>
                  <p className="text-gold font-heading text-base leading-relaxed mb-1 italic">{h.shloka}</p>
                  {h.shloka_meaning && <p className="text-sm text-text-muted">— {h.shloka_meaning}</p>}
                  {h.shloka_source && <p className="text-xs text-gold/70 mt-1.5">📿 {h.shloka_source}</p>}
                </div>
              )}

              <div className="grid sm:grid-cols-3 gap-3">
                {h.love && (
                  <div className="bg-white/3 rounded-xl p-4 border border-pink-500/15">
                    <div className="flex items-center gap-2 mb-2"><Heart className="w-4 h-4 text-pink-400" /><span className="text-text-main text-sm font-medium">{lang === 'hi' ? 'प्रेम' : 'Love'}</span></div>
                    <p className="text-text-muted text-sm leading-relaxed">{h.love}</p>
                  </div>
                )}
                {h.career && (
                  <div className="bg-white/3 rounded-xl p-4 border border-blue-500/15">
                    <div className="flex items-center gap-2 mb-2"><Briefcase className="w-4 h-4 text-blue-400" /><span className="text-text-main text-sm font-medium">{lang === 'hi' ? 'करियर' : 'Career'}</span></div>
                    <p className="text-text-muted text-sm leading-relaxed">{h.career}</p>
                  </div>
                )}
                {h.health && (
                  <div className="bg-white/3 rounded-xl p-4 border border-emerald-500/15">
                    <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-emerald-400" /><span className="text-text-main text-sm font-medium">{lang === 'hi' ? 'स्वास्थ्य' : 'Health'}</span></div>
                    <p className="text-text-muted text-sm leading-relaxed">{h.health}</p>
                  </div>
                )}
              </div>

              {/* Vaidik Upay — DISABLED per Guruji's request (Feb 2026). */}
              {false && h.vaidik_upay && h.vaidik_upay.beej_mantra && (
                <div className="mt-4">
                  <VaidikUpayCard upay={h.vaidik_upay} compact lang={lang} />
                </div>
              )}
            </div>
            <div className="text-center mt-6">
              <Link to="/daily-rashifal" className="btn-gold inline-flex items-center gap-2" data-testid="horoscope-consult-btn">
                {lang === 'hi' ? 'पूरा विस्तृत फलादेश पढ़ें' : 'Read Full Detailed Forecast'} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        ) : (
          !loading && rashifal.length > 0 && (
            <div className="text-center py-8">
              <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-white/3 border border-gold/10">
                <Sparkles className="w-5 h-5 text-gold" />
                <p className="text-text-muted">{t('horoscope.selectSign')}</p>
              </div>
            </div>
          )
        )}

        {/* CTA to full rashifal page */}
        <div className="text-center mt-10">
          <Link to="/daily-rashifal" className="btn-gold inline-flex items-center gap-2" data-testid="full-rashifal-link">
            <Star className="w-4 h-4" /> {lang === 'hi' ? 'पूरा राशिफल उपाय सहित देखें' : 'View Full Horoscope with Remedies'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
